import React, { useState } from 'react';
import { 
  Users, 
  UserPlus, 
  Search, 
  Filter, 
  Phone, 
  Mail, 
  MapPin, 
  DollarSign, 
  Calendar, 
  FileText, 
  ClipboardList, 
  Receipt as ReceiptIcon, 
  ArrowLeft, 
  MessageCircle, 
  Navigation, 
  ExternalLink, 
  Copy, 
  ChevronRight, 
  Tag, 
  Plus, 
  X, 
  Check, 
  Trash2, 
  Edit3, 
  Bot, 
  Zap,
  ShieldCheck,
  Car,
  CheckCircle2,
  AlertCircle,
  Building2,
  HeartHandshake
} from 'lucide-react';
import { Client, ClientSource, VehicleData, BeautyTechnicalCard } from '../types';
import { formatCurrencyBRL } from '../../../utils/formatUtils';
import { fetchCompanyByCnpj, fetchAddressByCep, CompanyResult, AddressResult } from '../services/publicDataServices';
import ScreenTourButton from '../../../components/tour/ScreenTourButton';
import { useTeamPermissions } from '../../../contexts/TeamPermissionsContext';
import { consumeClientDraft, BRIDGE_EVENTS } from '../utils/commandBridge';
import { supabase } from '../../../lib/supabase';

interface ClientManagerProps {
  clients: Client[];
  onSaveClient: (client: Client) => Promise<void> | void;
  onDeleteClient?: (clientId: string) => Promise<void> | void;
  onCreateQuoteForClient?: (client: Client) => void;
  onOpenQuote?: (client: Client) => void;
  onCreateWorkOrderForClient?: (client: Client) => void;
  onCreateAppointmentForClient?: (client: Client) => void;
  onCreateReceiptForClient?: (client: Client) => void;
  onBack: () => void;
}

export default function ClientManager({
  clients,
  onSaveClient,
  onDeleteClient,
  onCreateQuoteForClient,
  onCreateWorkOrderForClient,
  onCreateAppointmentForClient,
  onCreateReceiptForClient,
  onBack
}: ClientManagerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState<string>('all');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Partial<Client> | null>(null);
  const [copiedAddress, setCopiedAddress] = useState(false);

  // Estados de Busca Automática (Zero Digitação)
  const [isSearchingCnpj, setIsSearchingCnpj] = useState(false);
  const [isSearchingCep, setIsSearchingCep] = useState(false);
  const [autoFillMessage, setAutoFillMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [cepInput, setCepInput] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const { can } = useTeamPermissions();
  const canDelete = can('delete_records');

  const maskPhone = (val: string) => {
    let digits = val.replace(/\D/g, '');
    if (digits.startsWith('0') && (digits.length === 11 || digits.length === 12)) digits = digits.slice(1);
    if (digits.startsWith('55') && digits.length > 11) digits = digits.slice(2);
    if (digits.length > 11) digits = digits.slice(0, 11);
    if (digits.length <= 2) return digits.length > 0 ? `(${digits}` : '';
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  };

  const maskDocument = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 14);
    if (digits.length <= 11) {
      // CPF: 000.000.000-00
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
      if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
      return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
    }
    // CNPJ: 00.000.000/0001-00
    if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  };

  const maskCep = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  };

  const generateUuid = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // Ponte Omnibar -> CRM de Clientes: consome o rascunho e abre o modal pré-preenchido
  React.useEffect(() => {
    const checkDraft = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        const userId = user?.id || 'current_user';
        const draft = consumeClientDraft(userId);
        if (draft && draft.name) {
          setEditingClient({
            id: generateUuid(),
            userId,
            name: draft.name,
            phone: draft.phone ? maskPhone(draft.phone) : '',
            email: draft.email || '',
            document: draft.document ? maskDocument(draft.document) : '',
            address: draft.address || '',
            tags: ['Novo'],
            totalSpent: 0,
            ordersCount: 0,
            source: 'manual',
            notes: draft.notes || ''
          });
          setCepInput('');
          setAutoFillMessage({ type: 'success', text: 'Dados do cliente importados da linha de comando! Confira e confirme o salvamento.' });
          setIsEditModalOpen(true);
        }
      } catch (err) {
        console.warn('[ClientManager] Erro ao consumir rascunho de cliente:', err);
      }
    };

    checkDraft();
    window.addEventListener(BRIDGE_EVENTS.clients, checkDraft);
    return () => window.removeEventListener(BRIDGE_EVENTS.clients, checkDraft);
  }, []);

  // Helper de Retenção & Reativação (Dias desde o último atendimento)
  const getRetentionStatus = (client: Client) => {
    const lastDateStr = client.lastInteractionAt || client.createdAt;
    if (!lastDateStr) return { days: 0, status: 'ok', label: 'Recente', badgeColor: 'bg-emerald-50 text-emerald-800 border-emerald-200' };
    
    const lastDate = new Date(lastDateStr).getTime();
    const now = Date.now();
    const diffDays = Math.max(0, Math.floor((now - lastDate) / (1000 * 60 * 60 * 24)));

    if (diffDays >= 35) {
      return { days: diffDays, status: 'lost', label: `🔴 Sumido há ${diffDays}d`, badgeColor: 'bg-red-50 text-red-800 border-red-200' };
    } else if (diffDays >= 21) {
      return { days: diffDays, status: 'warning', label: `🟡 Atenção (${diffDays}d)`, badgeColor: 'bg-amber-50 text-amber-800 border-amber-200' };
    }
    return { days: diffDays, status: 'ok', label: `🟢 Há ${diffDays === 0 ? 'pouco' : `${diffDays}d`}`, badgeColor: 'bg-emerald-50 text-emerald-800 border-emerald-200' };
  };

  // Filtros de Clientes
  const filteredClients = clients.filter(client => {
    const matchesSearch = 
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (client.phone && client.phone.includes(searchTerm)) ||
      (client.document && client.document.includes(searchTerm)) ||
      (client.address && client.address.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (client.vehicleData && client.vehicleData.plate.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (client.vehicleData && client.vehicleData.model.toLowerCase().includes(searchTerm.toLowerCase()));

    const retention = getRetentionStatus(client);

    const matchesTag = 
      selectedTag === 'all' || 
      (selectedTag === 'lead' && client.source === 'chatbot_vitrine') ||
      (selectedTag === 'reactivation' && retention.days >= 21) ||
      (selectedTag === 'vip_club' && client.subscriptionStatus === 'active') ||
      (client.tags && client.tags.includes(selectedTag));

    return matchesSearch && matchesTag;
  });

  const missingClientsCount = clients.filter(c => getRetentionStatus(c).days >= 21).length;
  const vipClubCount = clients.filter(c => c.subscriptionStatus === 'active').length;

  const allTags = Array.from(
    new Set(clients.flatMap(c => c.tags || []))
  );

  const handleOpenNewModal = () => {
    setEditingClient({
      id: generateUuid(),
      userId: 'current_user',
      name: '',
      phone: '',
      email: '',
      document: '',
      address: '',
      tags: ['Novo'],
      totalSpent: 0,
      ordersCount: 0,
      source: 'manual',
      notes: ''
    });
    setCepInput('');
    setAutoFillMessage(null);
    setIsEditModalOpen(true);
  };

  const handleOpenEditModal = (client: Client) => {
    setEditingClient({ ...client });
    setCepInput('');
    setAutoFillMessage(null);
    setIsEditModalOpen(true);
  };

  // Exclusão de Cliente com Confirmação
  const handleDeleteClientAction = async (client: Client) => {
    if (!onDeleteClient) return;
    const confirmDelete = window.confirm(`Tem certeza que deseja excluir o cadastro do cliente "${client.name}"?\nEsta ação não poderá ser desfeita.`);
    if (!confirmDelete) return;

    try {
      setIsDeleting(true);
      await onDeleteClient(client.id);
      setIsEditModalOpen(false);
      setSelectedClient(null);
    } catch (err) {
      console.error('Erro ao excluir cliente:', err);
      alert('Erro ao tentar excluir cliente.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Busca Inteligente de Dados da Empresa por CNPJ
  const handleAutoFillCnpj = async () => {
    if (!editingClient?.document) {
      setAutoFillMessage({ type: 'error', text: 'Informe o CNPJ no campo para buscar os dados da empresa.' });
      return;
    }

    setIsSearchingCnpj(true);
    setAutoFillMessage(null);

    const result: CompanyResult = await fetchCompanyByCnpj(editingClient.document);
    setIsSearchingCnpj(false);

    if (result.success) {
      setEditingClient(prev => ({
        ...prev,
        document: result.cnpj,
        name: result.razaoSocial,
        address: result.formattedAddress,
        phone: result.phone || prev?.phone || '',
        email: result.email || prev?.email || '',
        source: 'cnpj_lookup',
        companyData: {
          razaoSocial: result.razaoSocial,
          nomeFantasia: result.nomeFantasia,
          cnae: result.cnae
        },
        notes: prev?.notes ? `${prev.notes}\nFantasia: ${result.nomeFantasia}` : `Nome Fantasia: ${result.nomeFantasia}\nCNAE: ${result.cnae}`
      }));
      setAutoFillMessage({ type: 'success', text: 'Dados da empresa preenchidos com sucesso!' });
    } else {
      setAutoFillMessage({ type: 'error', text: result.error || 'Erro ao consultar CNPJ.' });
    }
  };

  // Busca Inteligente de Endereço por CEP
  const handleAutoFillCep = async () => {
    if (!cepInput.trim()) {
      setAutoFillMessage({ type: 'error', text: 'Digite o CEP para buscar o endereço.' });
      return;
    }

    setIsSearchingCep(true);
    setAutoFillMessage(null);

    const result: AddressResult = await fetchAddressByCep(cepInput);
    setIsSearchingCep(false);

    if (result.success) {
      setEditingClient(prev => ({
        ...prev,
        address: result.formattedAddress
      }));
      setAutoFillMessage({ type: 'success', text: `Endereço localizado: ${result.city} - ${result.state}` });
    } else {
      setAutoFillMessage({ type: 'error', text: result.error || 'CEP não encontrado.' });
    }
  };

  // Importação Direta da Agenda Telefônica do Celular (Web Contacts API)
  const handleImportDeviceContact = async () => {
    if ('contacts' in navigator && 'ContactsManager' in window) {
      try {
        const props = ['name', 'tel', 'email', 'address'];
        const opts = { multiple: false };
        // @ts-ignore
        const contacts = await navigator.contacts.select(props, opts);
        if (contacts && contacts.length > 0) {
          const contact = contacts[0];
          const name = contact.name && contact.name[0] ? contact.name[0] : '';
          const phone = contact.tel && contact.tel[0] ? contact.tel[0] : '';
          const email = contact.email && contact.email[0] ? contact.email[0] : '';
          const rawAddress = contact.address && contact.address[0] ? contact.address[0] : '';
          const address = typeof rawAddress === 'string' ? rawAddress : (rawAddress ? `${rawAddress.street || ''} ${rawAddress.city || ''}`.trim() : '');

          setEditingClient(prev => ({
            ...prev,
            name: name || prev?.name || '',
            phone: phone || prev?.phone || '',
            email: email || prev?.email || '',
            address: address || prev?.address || '',
            source: 'agenda'
          }));
          setAutoFillMessage({ type: 'success', text: `Contato "${name || 'Selecionado'}" importado com sucesso da agenda!` });
        }
      } catch (err) {
        console.warn('Seleção de contatos cancelada ou erro:', err);
      }
    } else {
      setAutoFillMessage({ 
        type: 'error', 
        text: 'Importação rápida da agenda disponível no Chrome/Android. Você também pode preencher os campos abaixo.' 
      });
    }
  };

  const handleSaveForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingClient || !editingClient.name?.trim()) {
      alert('Por favor, informe o nome do cliente.');
      return;
    }

    const compiledClient: Client = {
      id: editingClient.id || generateUuid(),
      userId: editingClient.userId || 'current_user',
      name: editingClient.name.trim(),
      phone: editingClient.phone?.trim() || undefined,
      email: editingClient.email?.trim() || undefined,
      document: editingClient.document?.trim() || undefined,
      address: editingClient.address?.trim() || undefined,
      tags: editingClient.tags || [],
      totalSpent: editingClient.totalSpent || 0,
      ordersCount: editingClient.ordersCount || 0,
      source: (editingClient.source as ClientSource) || 'manual',
      notes: editingClient.notes?.trim() || undefined,
      vehicleData: editingClient.vehicleData,
      vehicleHistory: editingClient.vehicleHistory,
      beautyCard: editingClient.beautyCard,
      companyData: editingClient.companyData,
      createdAt: editingClient.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    await onSaveClient(compiledClient);
    setIsEditModalOpen(false);
    if (selectedClient && selectedClient.id === compiledClient.id) {
      setSelectedClient(compiledClient);
    }
  };

  const handleOpenWhatsApp = (phone?: string, name?: string) => {
    if (!phone) {
      alert('Telefone do cliente não cadastrado.');
      return;
    }
    const cleanPhone = phone.replace(/\D/g, '');
    const normalizedPhone = cleanPhone.startsWith('55') && cleanPhone.length > 11 ? cleanPhone : `55${cleanPhone}`;
    const text = `Olá, *${name || 'Cliente'}*! Tudo bem?`;
    window.open(`https://api.whatsapp.com/send?phone=${normalizedPhone}&text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleReactivateWhatsApp = (e: React.MouseEvent, client: Client) => {
    e.stopPropagation();
    if (!client.phone) {
      alert('Telefone do cliente não cadastrado.');
      return;
    }
    const cleanPhone = client.phone.replace(/\D/g, '');
    const normalizedPhone = cleanPhone.startsWith('55') && cleanPhone.length > 11 ? cleanPhone : `55${cleanPhone}`;
    const firstName = client.name.split(' ')[0];
    const retention = getRetentionStatus(client);
    const text = `Fala, *${firstName}*! Tudo bem? 💈\n\nNotamos que já faz ${retention.days} dias desde o seu último atendimento com a gente. Que tal garantir seu horário para essa semana?\n\n📅 Agende em 1 minuto pelo nosso link rápido: https://rmax.link/agendar`;
    window.open(`https://api.whatsapp.com/send?phone=${normalizedPhone}&text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleOpenGoogleMaps = (address: string) => {
    const encoded = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank');
  };

  const handleOpenWaze = (address: string) => {
    const encoded = encodeURIComponent(address);
    window.open(`https://waze.com/ul?q=${encoded}&navigate=yes`, '_blank');
  };

  const handleCopyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(true);
    setTimeout(() => setCopiedAddress(false), 3000);
  };

  const getSourceBadge = (source: ClientSource) => {
    switch (source) {
      case 'chatbot_vitrine':
        return { label: 'Chatbot da Vitrine', bg: 'bg-emerald-50 text-emerald-800 border-emerald-200', icon: Bot };
      case 'orcamento':
        return { label: 'Orçamento', bg: 'bg-blue-50 text-blue-800 border-blue-200', icon: FileText };
      case 'agenda':
        return { label: 'Agenda', bg: 'bg-purple-50 text-purple-800 border-purple-200', icon: Calendar };
      case 'os':
        return { label: 'Ordem de Serviço', bg: 'bg-amber-50 text-amber-800 border-amber-200', icon: ClipboardList };
      case 'cnpj_lookup':
        return { label: 'Cadastro Empresarial', bg: 'bg-indigo-50 text-indigo-800 border-indigo-200', icon: Building2 };
      default:
        return { label: 'Manual', bg: 'bg-zinc-100 text-zinc-700 border-zinc-200', icon: Users };
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 pb-20 font-sans">
      {/* Header Sticky Slim (56px) - Padrão R-MAX Mobile */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-zinc-200/80 px-4 sm:px-6 h-14 flex items-center shadow-2xs w-full">
        <div className="max-w-5xl mx-auto w-full flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <button
              type="button"
              onClick={onBack}
              className="p-2 -ml-1 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-xl transition-all active:scale-95 cursor-pointer shrink-0"
              title="Voltar ao Painel"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-sm sm:text-base font-black text-zinc-900 truncate tracking-tight">
                Meus Clientes
              </h1>
              <p className="text-[11px] text-zinc-500 truncate font-medium">
                {clients.length === 0
                  ? 'Nenhum cliente cadastrado'
                  : `${clients.length} ${clients.length === 1 ? 'cliente cadastrado' : 'clientes cadastrados'}`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <ScreenTourButton screenKey="clients" />
            <button
              type="button"
              onClick={handleOpenNewModal}
              className="px-3.5 py-2 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-sm shadow-brand/25 flex items-center gap-1.5 cursor-pointer active:scale-95"
              title="Cadastrar Novo Cliente"
            >
              <UserPlus className="h-4 w-4" />
              <span className="font-extrabold">Novo</span>
            </button>
          </div>
        </div>
      </header>

      {/* Container Principal Harmonizado (max-w-5xl) */}
      <main className="max-w-5xl mx-auto w-full px-4 sm:px-6 pt-4 space-y-3.5">
        {clients.length > 0 && (
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <input
              type="text"
              placeholder="Buscar por nome ou telefone..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full bg-white border border-zinc-200/90 rounded-2xl pl-10 pr-4 py-2.5 text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20 shadow-2xs h-11"
            />
          </div>
        )}

        {/* Filtros em Pills (Apenas se houver clientes) */}
        {clients.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none text-xs">
            <button
              type="button"
              onClick={() => setSelectedTag('all')}
              className={`px-3.5 py-1.5 rounded-xl font-bold transition-all cursor-pointer whitespace-nowrap text-xs ${
                selectedTag === 'all'
                  ? 'bg-brand text-white shadow-xs'
                  : 'bg-white text-zinc-600 border border-zinc-200/70 hover:bg-zinc-50'
              }`}
            >
              Todos ({clients.length})
            </button>

            {missingClientsCount > 0 && (
              <button
                type="button"
                onClick={() => setSelectedTag('reactivation')}
                className={`px-3.5 py-1.5 rounded-xl font-bold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 text-xs ${
                  selectedTag === 'reactivation'
                    ? 'bg-amber-600 text-white shadow-xs'
                    : 'bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100'
                }`}
              >
                <span>🟡 Retorno Pendente (+21 dias)</span>
                <span className="bg-white/20 px-1.5 py-0.2 rounded-full text-[10px]">
                  {missingClientsCount}
                </span>
              </button>
            )}

            {vipClubCount > 0 && (
              <button
                type="button"
                onClick={() => setSelectedTag('vip_club')}
                className={`px-3.5 py-1.5 rounded-xl font-bold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 text-xs ${
                  selectedTag === 'vip_club'
                    ? 'bg-amber-600 text-white shadow-xs'
                    : 'bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100'
                }`}
              >
                <span>👑 Mensalistas</span>
                <span className="bg-white/20 px-1.5 py-0.2 rounded-full text-[10px]">
                  {vipClubCount}
                </span>
              </button>
            )}
          </div>
        )}

        {/* Lista de Clientes (Grid de Cards ou Estado Vazio) */}
        {filteredClients.length === 0 ? (
          <div className="py-14 bg-white border border-dashed border-zinc-200 rounded-3xl text-center p-6 flex flex-col items-center justify-center space-y-3">
            <div className="w-14 h-14 rounded-2xl bg-brand/10 text-brand flex items-center justify-center">
              <Users className="h-7 w-7" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-black text-zinc-900">
                {searchTerm ? 'Nenhum cliente encontrado com esse nome' : 'Sua lista de clientes está vazia'}
              </p>
              <p className="text-xs text-zinc-500 max-w-xs mx-auto leading-relaxed">
                {searchTerm 
                  ? 'Verifique se digitou o nome ou número corretamente.' 
                  : 'Cadastre seus clientes para salvar telefones, endereços e o histórico de atendimentos.'}
              </p>
            </div>
            {!searchTerm && (
              <button
                type="button"
                onClick={handleOpenNewModal}
                className="mt-2 px-5 py-2.5 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-sm active:scale-95 cursor-pointer flex items-center gap-2"
              >
                <UserPlus className="h-4 w-4" />
                <span>Cadastrar Primeiro Cliente</span>
              </button>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3.5">
            {filteredClients.map(client => {
              const sourceBadge = getSourceBadge(client.source);
              const SourceIcon = sourceBadge.icon;
              const retention = getRetentionStatus(client);

              return (
                <div
                  key={client.id}
                  onClick={() => setSelectedClient(client)}
                  className="p-4 sm:p-5 bg-white border border-zinc-100 hover:border-zinc-200 rounded-2xl space-y-3.5 shadow-sm hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between active:scale-98"
                >
                  <div className="space-y-2.5">
                    {/* Header do Card com Origem, Retenção e Tags */}
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-black px-2.5 py-0.5 rounded-full border ${sourceBadge.bg}`}>
                          <SourceIcon className="h-3 w-3" />
                          {sourceBadge.label}
                        </span>

                        <span className={`inline-flex items-center text-[10px] font-black px-2 py-0.5 rounded-full border ${retention.badgeColor}`}>
                          {retention.label}
                        </span>

                        {client.subscriptionStatus === 'active' && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                            👑 VIP
                          </span>
                        )}
                      </div>

                      {client.tags && client.tags.length > 0 && (
                        <div className="flex gap-1">
                          {client.tags.slice(0, 2).map((t, idx) => (
                            <span key={idx} className="text-[9px] font-bold bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-md">
                              #{t}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Nome e Avatar */}
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-2xl bg-brand/10 text-brand-dark flex items-center justify-center font-black text-sm shrink-0 border border-brand/20 group-hover:scale-105 transition-transform">
                        {client.name.substring(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-bold text-zinc-900 group-hover:text-brand transition-colors truncate">
                          {client.name}
                        </h3>
                        {client.phone && (
                          <p className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5 font-medium">
                            <Phone className="h-3 w-3 text-brand" />
                            <span>{client.phone}</span>
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Destaque Veicular no Card (Oficinas) */}
                    {client.vehicleData && (
                      <div className="p-2 bg-zinc-50 rounded-xl border border-zinc-100 flex items-center justify-between text-[11px]">
                        <div className="flex items-center gap-1.5 font-bold text-zinc-800">
                          <Car className="h-3.5 w-3.5 text-blue-600" />
                          <span>{client.vehicleData.model}</span>
                        </div>
                        <span className="font-mono font-black text-[10px] bg-blue-50 text-blue-800 border border-blue-100 px-1.5 py-0.5 rounded">
                          {client.vehicleData.plate}
                        </span>
                      </div>
                    )}

                    {/* Endereço Resumido */}
                    {client.address && (
                      <p className="text-[11px] text-zinc-400 flex items-center gap-1 line-clamp-1 font-medium">
                        <MapPin className="h-3 w-3 text-zinc-400 shrink-0" />
                        <span>{client.address}</span>
                      </p>
                    )}
                  </div>

                  {/* Rodapé Financeiro do Cliente & Ações de Reativação */}
                  <div className="pt-3 border-t border-zinc-100 flex items-center justify-between text-xs gap-2">
                    <div>
                      <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block">Total em Compras e Serviços</span>
                      <span className="font-black text-brand text-sm">{formatCurrencyBRL(client.totalSpent)}</span>
                    </div>

                    <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                      {client.phone && retention.days >= 21 && (
                        <button
                          type="button"
                          onClick={(e) => handleReactivateWhatsApp(e, client)}
                          className="px-2.5 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-xl border border-red-200 transition-all font-bold text-[10px] flex items-center gap-1 cursor-pointer active:scale-95 shadow-2xs"
                          title="Enviar convite de retorno com link da vitrine"
                        >
                          <MessageCircle className="h-3.5 w-3.5" />
                          <span>Reativar</span>
                        </button>
                      )}

                      {client.phone && retention.days < 21 && (
                        <button
                          type="button"
                          onClick={() => handleOpenWhatsApp(client.phone, client.name)}
                          className="p-2 bg-emerald-50 hover:bg-emerald-100 text-brand rounded-xl border border-emerald-100 transition-all cursor-pointer active:scale-95"
                          title="WhatsApp"
                        >
                          <MessageCircle className="h-4 w-4" />
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => setSelectedClient(client)}
                        className="p-2 bg-zinc-50 hover:bg-zinc-100 text-zinc-600 rounded-xl border border-zinc-100 transition-all cursor-pointer active:scale-95"
                        title="Ver Ficha Completa"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* DRAWER / DETALHES DO CLIENTE & FICHA TÉCNICA ESPECIALISTA                */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {selectedClient && (
        <div className="fixed inset-0 z-100 bg-neutral-950/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="relative w-full sm:max-w-xl bg-white border border-zinc-100 rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="p-5 bg-brand text-white flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-white/20 text-white flex items-center justify-center font-black text-base border border-white/30">
                  {selectedClient.name.substring(0, 2).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-base font-black text-white">{selectedClient.name}</h3>
                  {selectedClient.document && (
                    <p className="text-xs text-white/80 font-mono">{selectedClient.document}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => handleOpenEditModal(selectedClient)}
                  className="p-2 text-white/90 hover:text-white rounded-xl hover:bg-white/10 transition-colors cursor-pointer"
                  title="Editar Cliente"
                >
                  <Edit3 className="h-4 w-4" />
                </button>
                {onDeleteClient && canDelete && (
                  <button
                    type="button"
                    onClick={() => handleDeleteClientAction(selectedClient)}
                    disabled={isDeleting}
                    className="p-2 text-white/80 hover:text-red-200 rounded-xl hover:bg-white/10 transition-colors cursor-pointer disabled:opacity-50"
                    title="Excluir Cliente"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setSelectedClient(null)}
                  className="p-2 text-white/90 hover:text-white rounded-full hover:bg-white/10 transition-colors cursor-pointer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Corpo do Drawer */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-zinc-50 text-xs">
              {/* 1. Métricas Rápidas */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white p-3.5 rounded-2xl border border-zinc-100 shadow-sm">
                  <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block">Total em Compras e Serviços</span>
                  <span className="text-base font-black text-brand">{formatCurrencyBRL(selectedClient.totalSpent)}</span>
                </div>
                <div className="bg-white p-3.5 rounded-2xl border border-zinc-100 shadow-sm">
                  <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block">Serviços Realizados</span>
                  <span className="text-base font-black text-zinc-900">{selectedClient.ordersCount} realizados</span>
                </div>
              </div>

              {/* 2. Ficha Técnica Veicular (Para Oficinas/Mecânicas) */}
              {selectedClient.vehicleData && (
                <div className="bg-white p-4 rounded-2xl border border-blue-100 space-y-3 shadow-sm">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-black text-blue-900 uppercase tracking-wider flex items-center gap-1.5">
                      <Car className="h-4 w-4 text-blue-600" />
                      Veículo Vinculado ao Cliente
                    </h4>
                    <span className="font-mono font-black text-xs bg-blue-50 text-blue-800 px-2 py-0.5 rounded-md border border-blue-100">
                      {selectedClient.vehicleData.plate}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-zinc-700">
                    <p><strong className="text-zinc-500">Modelo:</strong> {selectedClient.vehicleData.model}</p>
                    <p><strong className="text-zinc-500">Ano:</strong> {selectedClient.vehicleData.modelYear}</p>
                    <p><strong className="text-zinc-500">Cor:</strong> {selectedClient.vehicleData.color}</p>
                    {selectedClient.vehicleData.engine && <p><strong className="text-zinc-500">Motor:</strong> {selectedClient.vehicleData.engine}</p>}
                    {selectedClient.vehicleData.currentMileageKm && (
                      <p className="col-span-2"><strong className="text-zinc-500">Última KM Registrada:</strong> {selectedClient.vehicleData.currentMileageKm.toLocaleString('pt-BR')} km</p>
                    )}
                  </div>
                </div>
              )}

              {/* 3. Ficha Técnica de Salão / Estética */}
              {selectedClient.beautyCard && (
                <div className="bg-white p-4 rounded-2xl border border-rose-100 space-y-2.5 shadow-sm">
                  <h4 className="text-xs font-black text-rose-900 uppercase tracking-wider flex items-center gap-1.5">
                    <HeartHandshake className="h-4 w-4 text-rose-600" />
                    Ficha Técnica de Beleza & Estética
                  </h4>
                  <div className="space-y-1 text-zinc-700">
                    {selectedClient.beautyCard.hairColorFormula && (
                      <p><strong className="text-zinc-500">Fórmula de Coloração:</strong> {selectedClient.beautyCard.hairColorFormula}</p>
                    )}
                    {selectedClient.beautyCard.nailShape && (
                      <p><strong className="text-zinc-500">Formato / Alongamento Unha:</strong> {selectedClient.beautyCard.nailShape} ({selectedClient.beautyCard.nailTechnique || 'Gel'})</p>
                    )}
                    {selectedClient.beautyCard.allergiesRestrictions && (
                      <p className="text-rose-700 font-bold">⚠️ Alergias: {selectedClient.beautyCard.allergiesRestrictions}</p>
                    )}
                  </div>
                </div>
              )}

              {/* 4. Contatos & Endereço com GPS */}
              <div className="bg-white p-4 rounded-2xl border border-zinc-100 space-y-3 shadow-sm">
                <h4 className="text-xs font-black text-zinc-800 uppercase tracking-wider">Contatos & Localização</h4>

                <div className="space-y-2 text-zinc-700">
                  {selectedClient.phone && (
                    <div className="flex items-center justify-between p-2.5 bg-zinc-50 rounded-xl border border-zinc-100">
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-brand" />
                        <span className="font-bold">{selectedClient.phone}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleOpenWhatsApp(selectedClient.phone, selectedClient.name)}
                        className="text-[10px] font-black bg-brand text-white px-3 py-1.5 rounded-lg hover:bg-brand-dark active:scale-95 transition-all cursor-pointer"
                      >
                        Chamar no WhatsApp
                      </button>
                    </div>
                  )}

                  {selectedClient.email && (
                    <div className="flex items-center gap-2 p-2.5 bg-zinc-50 rounded-xl border border-zinc-100">
                      <Mail className="h-4 w-4 text-zinc-400" />
                      <span>{selectedClient.email}</span>
                    </div>
                  )}

                  {selectedClient.address && (
                    <div className="space-y-2 p-3 bg-zinc-50 rounded-xl border border-zinc-100">
                      <div className="flex items-center justify-between">
                        <span className="font-bold flex items-center gap-1.5 text-zinc-800">
                          <MapPin className="h-4 w-4 text-brand" />
                          Endereço Cadastrado
                        </span>
                        <button
                          type="button"
                          onClick={() => handleCopyAddress(selectedClient.address!)}
                          className="text-[10px] text-brand font-bold hover:underline flex items-center gap-1 cursor-pointer"
                        >
                          <Copy className="h-3 w-3" />
                          <span>{copiedAddress ? 'Copiado!' : 'Copiar'}</span>
                        </button>
                      </div>

                      <p className="text-zinc-600">{selectedClient.address}</p>

                      <div className="grid grid-cols-2 gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => handleOpenGoogleMaps(selectedClient.address!)}
                          className="py-2.5 px-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 shadow-xs cursor-pointer active:scale-95 transition-all"
                        >
                          <Navigation className="h-3.5 w-3.5" />
                          <span>Google Maps</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => handleOpenWaze(selectedClient.address!)}
                          className="py-2.5 px-3 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 shadow-xs cursor-pointer active:scale-95 transition-all"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          <span>Abrir Waze</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* 5. Ações Rápidas de Criação Direta para este Cliente */}
              <div className="bg-white p-4 rounded-2xl border border-zinc-100 space-y-2.5 shadow-sm">
                <h4 className="text-[10px] font-black text-zinc-900 uppercase tracking-widest">Criar Novo Documento</h4>

                <div className="grid grid-cols-2 gap-2">
                  {onCreateQuoteForClient && (
                    <button
                      type="button"
                      onClick={() => {
                        onCreateQuoteForClient(selectedClient);
                        setSelectedClient(null);
                      }}
                      className="p-3 bg-zinc-50 hover:bg-blue-50 text-zinc-800 hover:text-blue-700 border border-zinc-100 hover:border-blue-200 rounded-xl font-bold flex items-center gap-2 transition-all cursor-pointer text-left active:scale-95"
                    >
                      <FileText className="h-4 w-4 text-blue-600 shrink-0" />
                      <span>Novo Orçamento</span>
                    </button>
                  )}

                  {onCreateWorkOrderForClient && (
                    <button
                      type="button"
                      onClick={() => {
                        onCreateWorkOrderForClient(selectedClient);
                        setSelectedClient(null);
                      }}
                      className="p-3 bg-zinc-50 hover:bg-purple-50 text-zinc-800 hover:text-purple-700 border border-zinc-100 hover:border-purple-200 rounded-xl font-bold flex items-center gap-2 transition-all cursor-pointer text-left active:scale-95"
                    >
                      <ClipboardList className="h-4 w-4 text-purple-600 shrink-0" />
                      <span>Nova OS / Fotos</span>
                    </button>
                  )}

                  {onCreateAppointmentForClient && (
                    <button
                      type="button"
                      onClick={() => {
                        onCreateAppointmentForClient(selectedClient);
                        setSelectedClient(null);
                      }}
                      className="p-3 bg-zinc-50 hover:bg-teal-50 text-zinc-800 hover:text-teal-700 border border-zinc-100 hover:border-teal-200 rounded-xl font-bold flex items-center gap-2 transition-all cursor-pointer text-left active:scale-95"
                    >
                      <Calendar className="h-4 w-4 text-teal-600 shrink-0" />
                      <span>Agendar Horário</span>
                    </button>
                  )}

                  {onCreateReceiptForClient && (
                    <button
                      type="button"
                      onClick={() => {
                        onCreateReceiptForClient(selectedClient);
                        setSelectedClient(null);
                      }}
                      className="p-3 bg-zinc-50 hover:bg-emerald-50 text-zinc-800 hover:text-brand border border-zinc-100 hover:border-emerald-200 rounded-xl font-bold flex items-center gap-2 transition-all cursor-pointer text-left active:scale-95"
                    >
                      <ReceiptIcon className="h-4 w-4 text-brand shrink-0" />
                      <span>Emitir Recibo</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* MODAL DE CADASTRO / EDIÇÃO DE CLIENTE (COM ZERO DIGITAÇÃO CNPJ & CEP)    */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {isEditModalOpen && editingClient && (
        <div className="fixed inset-0 z-110 bg-neutral-950/85 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={handleSaveForm} className="relative w-full max-w-lg bg-white border border-zinc-100 rounded-3xl p-6 space-y-4 shadow-2xl text-xs max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
              <h3 className="text-sm font-black text-zinc-900">
                {editingClient.name ? `Editar: ${editingClient.name}` : 'Novo Cliente'}
              </h3>
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="p-1.5 text-zinc-400 hover:text-zinc-700 rounded-full cursor-pointer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Atalho Opcional: Importar Contato da Agenda do Celular */}
            <button
              type="button"
              onClick={handleImportDeviceContact}
              className="w-full py-2.5 px-3 bg-emerald-50 hover:bg-emerald-100 text-brand border border-emerald-200/80 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all cursor-pointer text-center active:scale-95 text-xs"
            >
              <Phone className="h-4 w-4 text-brand" />
              <span>Puxar Contato da Agenda do Celular</span>
            </button>

            {autoFillMessage && (
              <div className={`p-2.5 rounded-xl text-[11px] font-medium flex items-center gap-1.5 ${
                autoFillMessage.type === 'success' 
                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
                  : 'bg-rose-50 text-rose-800 border border-rose-200'
              }`}>
                {autoFillMessage.type === 'success' ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> : <AlertCircle className="h-3.5 w-3.5 shrink-0" />}
                <span>{autoFillMessage.text}</span>
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block mb-1">
                  Nome do Cliente *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Maria Silva ou João Pereira"
                  value={editingClient.name || ''}
                  onChange={e => setEditingClient({ ...editingClient, name: e.target.value })}
                  className="w-full bg-zinc-50 border border-zinc-200/80 rounded-xl px-3.5 py-2.5 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20 font-medium text-xs"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block mb-1">
                    WhatsApp / Telefone
                  </label>
                  <input
                    type="text"
                    placeholder="(11) 99999-9999"
                    value={editingClient.phone || ''}
                    onChange={e => setEditingClient({ ...editingClient, phone: maskPhone(e.target.value) })}
                    className="w-full bg-zinc-50 border border-zinc-200/80 rounded-xl px-3.5 py-2.5 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20 font-medium text-xs"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block mb-1">
                    CPF ou CNPJ (Opcional)
                  </label>
                  <input
                    type="text"
                    placeholder="000.000.000-00"
                    value={editingClient.document || ''}
                    onChange={e => setEditingClient({ ...editingClient, document: maskDocument(e.target.value) })}
                    className="w-full bg-zinc-50 border border-zinc-200/80 rounded-xl px-3.5 py-2.5 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20 font-medium font-mono text-xs"
                  />
                </div>
              </div>

              {/* Endereço com Busca Rápida de CEP */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block">
                    Endereço Completo (Opcional)
                  </label>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="text"
                      placeholder="CEP (00000-000)"
                      value={cepInput}
                      onChange={e => setCepInput(maskCep(e.target.value))}
                      className="w-28 bg-zinc-100 border border-zinc-200 rounded-lg px-2 py-0.5 text-[11px] font-mono"
                    />
                    <button
                      type="button"
                      onClick={handleAutoFillCep}
                      disabled={isSearchingCep}
                      className="px-2.5 py-0.5 bg-zinc-200 hover:bg-zinc-300 text-zinc-800 font-bold text-[10px] rounded-lg cursor-pointer active:scale-95 disabled:opacity-50"
                    >
                      {isSearchingCep ? '...' : 'Buscar CEP'}
                    </button>
                  </div>
                </div>
                <input
                  type="text"
                  placeholder="Rua, Número, Bairro, Cidade"
                  value={editingClient.address || ''}
                  onChange={e => setEditingClient({ ...editingClient, address: e.target.value })}
                  className="w-full bg-zinc-50 border border-zinc-200/80 rounded-xl px-3.5 py-2.5 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20 font-medium text-xs"
                />
              </div>

              <div>
                <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block mb-1">
                  E-mail (Opcional)
                </label>
                <input
                  type="email"
                  placeholder="cliente@email.com"
                  value={editingClient.email || ''}
                  onChange={e => setEditingClient({ ...editingClient, email: e.target.value })}
                  className="w-full bg-zinc-50 border border-zinc-200/80 rounded-xl px-3.5 py-2.5 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20 font-medium text-xs"
                />
              </div>

              <div>
                <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block mb-1">
                  Anotações / Observações (Opcional)
                </label>
                <textarea
                  rows={2}
                  placeholder="Preferências do cliente, notas de atendimento ou lembretes..."
                  value={editingClient.notes || ''}
                  onChange={e => setEditingClient({ ...editingClient, notes: e.target.value })}
                  className="w-full bg-zinc-50 border border-zinc-200/80 rounded-xl p-3 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20 resize-none font-medium text-xs"
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-zinc-100">
              {editingClient.name && onDeleteClient && canDelete ? (
                <button
                  type="button"
                  onClick={() => handleDeleteClientAction(editingClient as Client)}
                  disabled={isDeleting}
                  className="px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl font-bold flex items-center gap-1.5 transition-colors cursor-pointer text-xs disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Excluir</span>
                </button>
              ) : <div />}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 text-zinc-500 hover:text-zinc-800 font-bold cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-sm shadow-brand/20 active:scale-95 transition-all cursor-pointer"
                >
                  Salvar Cliente
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
